import type { MutableRefObject } from 'react';
import { useEffect, useRef } from 'react';

interface UseImageLoadProps {
  imageRef: MutableRefObject<HTMLImageElement | null>;
  useImageObject: boolean;
  src: string; // useImageObject가 true일 때만 필수
  onLoadComplete?: () => void;
  onLoadError?: () => void;
  retryCount?: number;
  delayTime?: number;
}

/**
 * 이미지 로딩 상태를 관리하는 훅
 *
 * @description
 * 이미지의 로딩 완료 여부를 추적하고 관리합니다.
 * Image 객체 생성 또는 기존 img 엘리먼트의 로딩 상태를 처리합니다.
 *
 * 에러 발생 시 retryCount만큼 delayTime 간격으로 이미지 로드를 재시도합니다.
 * 모든 재시도가 실패하면 onLoadError 콜백이 호출됩니다.
 *
 * @example
 * // Image 객체 사용 시 (SpriteImage 등)
 * useImageLoad({
 *   imageRef,
 *   useImageObject: true,
 *   src: spriteImage,
 *   onLoadComplete: () => void,
 *   onLoadError: () => void,
 *   retryCount: number, // 재시도 횟수 (기본값: 0)
 *   delayTime: number, // 재시도 딜레이 (기본값: 0ms)
 * });
 *
 * // 기존 img 엘리먼트 사용 시 (next/image 등)
 * useImageLoad({
 *   imageRef,
 *   useImageObject: false,
 *   onLoadComplete: () => void,
 *   onLoadError: () => void,
 *   retryCount: number, // 재시도 횟수 (기본값: 0)
 *   delayTime: number, // 재시도 딜레이 (기본값: 0ms)
 * });
 */
export const useImageLoad = ({
  imageRef,
  useImageObject,
  src,
  onLoadComplete,
  onLoadError,
  retryCount = 0,
  delayTime = 0,
}: UseImageLoadProps) => {
  const retryCountRef = useRef(0);
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    retryCountRef.current = 0;

    // Image 객체 사용 시에만 새로운 Image 객체 생성
    if (useImageObject && src && !imageRef.current) {
      const newImage = new Image();
      imageRef.current = newImage;
      newImage.src = src;
    }

    const img = imageRef.current;
    if (!img) return;

    // 이미지 로딩 완료 체크
    const handleImageLoadComplete = () => {
      const isLoadComplete = img.complete && img.naturalWidth !== 0;

      if (isLoadComplete) {
        onLoadComplete?.();
        cleanup();
      }
    };

    // 이미지 로딩 실패 체크
    const handleImageLoadError = () => {
      if (retryCountRef.current < retryCount) {
        // 이미지 로딩 재시도 횟수 증가
        retryCountRef.current += 1;

        // 이미지 로딩 재시도 딜레이 계산
        const delay = delayTime;

        timeoutIdRef.current = setTimeout(() => {
          // 이미지 로딩 재시도
          if (imageRef.current) {
            imageRef.current.src = src;
          }
        }, delay);

        return;
      }

      // 이미지 로딩 실패 (최대 재시도 횟수 도달)
      console.error(`이미지 로딩 실패 (최대 재시도 횟수 도달): ${src}`);
      onLoadError?.();
      cleanup();
    };

    // 이벤트 리스너 제거
    const cleanup = () => {
      img.removeEventListener('load', handleImageLoadComplete);
      img.removeEventListener('error', handleImageLoadError);

      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };

    // 이미 로드된 경우 체크
    handleImageLoadComplete();

    // 이벤트 리스너 등록
    img.addEventListener('load', handleImageLoadComplete);
    img.addEventListener('error', handleImageLoadError);

    return cleanup;
  }, [useImageObject, src, onLoadComplete, onLoadError, retryCount, delayTime]);
};
